/**
 * <theme-toggle> — system / light / dark.
 *
 * ◆ Three states, not two. "System" is the default and must stay reachable: a
 * user who switched to dark once should not be locked out of following their OS
 * at sunrise. A two-state toggle silently destroys that preference.
 *
 * © Manish Jagdish Thatte
 */

import { THEMES } from '../config.js';
import { getSettings, setSetting, subscribe } from '../store.js';

const LABEL = { system: 'Auto', light: 'Light', dark: 'Dark' };

export class ThemeToggle extends HTMLElement {
  connectedCallback() {
    if (this._built) return;
    this._built = true;
    this.render();
    this._unsubscribe = subscribe('settings.theme', () => this.render());
  }

  disconnectedCallback() { this._unsubscribe?.(); }

  render() {
    this.textContent = '';
    const current = getSettings().theme;
    const group = document.createElement('div');
    group.className = 'segmented';
    group.setAttribute('role', 'group');
    group.setAttribute('aria-label', 'Colour theme');

    for (const theme of THEMES) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'segmented__option';
      b.textContent = LABEL[theme];
      b.setAttribute('aria-pressed', String(theme === current));
      b.addEventListener('click', () => setSetting('theme', theme));
      group.append(b);
    }
    this.append(group);
  }
}

/**
 * Put the theme on <html>.
 *
 * ⚠ `data-theme` is **removed** for 'system', not set to "system". The token
 * files key their dark block off `:root:not([data-theme="light"])` inside a
 * prefers-color-scheme query plus `:root[data-theme="dark"]`, so an unrecognised
 * attribute value would leave the page permanently light for a user whose OS is
 * dark.
 */
export function applyTheme(theme) {
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);
}

customElements.define('theme-toggle', ThemeToggle);
